import { apiClient, unwrap } from './client';

/** PAIE-1 — attestation et certificat de travail ; CONTRATS (20/10/2026) — contrat de travail, engagement journalier. */
export type TypeDocumentRh = 'attestation_travail' | 'certificat_travail' | 'contrat_travail' | 'engagement_journalier';

export interface DocumentRh {
  id:              string;
  employeeId:      string | null;
  ouvrierId:       string | null;
  type:            TypeDocumentRh;
  numero:          string;
  emisLe:          string;
  emisPar:         string | null;
  annuleLe:        string | null;
  motifAnnulation: string | null;
  employee:        { id: string; fullName: string; matricule: string } | null;
  ouvrier:         { id: string; nom: string; telephone: string } | null;
}

export type NatureContrat = 'cdi' | 'cdd' | 'stage';
export type CategorieContrat = 'ouvrier' | 'employe' | 'agent_maitrise' | 'technicien' | 'cadre';
export const LIBELLE_CATEGORIE: Record<CategorieContrat, string> = {
  ouvrier: 'Ouvrier (payé à l’heure ou à la journée)', employe: 'Employé (payé au mois)', agent_maitrise: 'Agent de maîtrise',
  technicien: 'Technicien ou assimilé', cadre: 'Cadre',
};
/** Durées d'essai validées (cabinet 20/10, correctif PDG 21/10), non renouvelables ; préavis du CDI = essai. */
const cddCourt = (debut: string, fin: string) => {
  if (!debut || !fin) return false;
  const [a, m, j] = debut.split('-').map(Number);
  return fin <= new Date(Date.UTC(a, m + 2, j - 1)).toISOString().slice(0, 10);
};
export const ESSAI_AFFICHE = (nature: NatureContrat, cat: CategorieContrat | '', debut = '', fin = ''): string =>
  nature === 'stage' ? '8 jours' : nature === 'cdd' ? (cddCourt(debut, fin) ? '8 jours' : '3 mois')
    : ({ ouvrier: '8 jours', employe: '1 mois', agent_maitrise: '1 mois', technicien: '3 mois', cadre: '6 mois', '': '—' } as const)[cat];

export interface PreremplissageContrat {
  nature: NatureContrat; dateDebut: string; dateFin: string | null; telephone: string | null;
  salaireBaseFcfa: number | null; primeTransportFcfa: number | null; heuresHebdo: number; villeLitiges: string | null;
  lieuTravail: string | null; categorieConvention: string | null; jobTitle: string;
}
export type SaisieContratApi = Record<string, unknown> & { employeeId: string; nature: NatureContrat };
export interface SaisieEngagementApi { ouvrierId: string; dateDebut: string; tache: string; lieu: string; equipe?: string; jourPaiement: string }

export const LIBELLE_DOCUMENT: Record<TypeDocumentRh, string> = {
  attestation_travail: 'Attestation de travail',
  certificat_travail:  'Certificat de travail',
  contrat_travail:     'Contrat de travail',
  engagement_journalier: 'Engagement journalier',
};

export const documentsRhApi = {
  lister:  async (employeeId?: string) => unwrap<DocumentRh[]>(await apiClient.get('/hr/documents', { params: { employeeId } })),
  listerOuvrier: async (ouvrierId: string) => unwrap<DocumentRh[]>(await apiClient.get('/hr/documents', { params: { ouvrierId } })),
  preremplirContrat: async (employeeId: string) =>
    unwrap<PreremplissageContrat>(await apiClient.get(`/hr/documents/contrat/preremplir/${employeeId}`)),
  emettreContrat: async (saisie: SaisieContratApi) => unwrap<{ id: string; numero: string }>(await apiClient.post('/hr/documents/contrat', saisie)),
  emettreEngagement: async (saisie: SaisieEngagementApi) =>
    unwrap<{ id: string; numero: string }>(await apiClient.post('/hr/documents/engagement', saisie)),
  emettre: async (employeeId: string, type: TypeDocumentRh) =>
    unwrap<{ id: string; numero: string }>(await apiClient.post('/hr/documents', { employeeId, type })),
  annuler: async (id: string, motif: string) => unwrap<{ id: string }>(await apiClient.post(`/hr/documents/${id}/annuler`, { motif })),
  /** Télécharge le PDF (même façon que les bons de commande : le jeton de connexion ne passe pas par un simple lien). */
  telecharger: async (id: string, numero: string) => {
    const r = await apiClient.get(`/hr/documents/${id}/pdf`, { responseType: 'blob' });
    const nom = String(r.headers['content-disposition'] ?? '').match(/filename="([^"]+)"/)?.[1] ?? `${numero}.pdf`;
    const url = URL.createObjectURL(r.data);
    const lien = document.createElement('a');
    lien.href = url; lien.download = nom;
    document.body.appendChild(lien); lien.click(); lien.remove();
    URL.revokeObjectURL(url);
  },
};
